import { HTMLAttributes } from 'react';
import clsx from 'clsx';
import { AmplienceImage } from '@/types/amplience';
import { LazyAmplienceResponsiveImage } from '@/components/ResponsiveImage/LazyAmplienceResponsiveImage';
import {
  getAmplienceImageSrcSet,
  getCssAspectRatio,
} from '@/components/ResponsiveImage/amplienceImageUtils';
import { getAmplienceImageUrl } from '@/utility/getImageUrl';

export type AmplienceResponsiveImageData = {
  src: AmplienceImage['image'] | string | null;
  sizes: string;
  widths?: number[];
  quality?: number;
  aspectRatio?: `${number}:${number}`;
};

export type AmplienceResponsiveImageDataWithMediaQuery = AmplienceResponsiveImageData & {
  media: string;
};

export type AmplienceResponsiveImageProps = Omit<
  HTMLAttributes<HTMLImageElement>,
  'alt' | 'src' | 'srcset' | 'sizes' | 'loading' | 'fetchPriority' | 'width' | 'height'
> & {
  source: AmplienceResponsiveImageData | AmplienceResponsiveImageDataWithMediaQuery[];
  alt: string;
  loading?: 'lazy' | 'eager';
  priority?: 'high' | 'low' | 'auto';
  render?: 'server-side' | 'client-side';
};

export const AmplienceResponsiveImage = ({
  className,
  source,
  alt,
  loading,
  priority,
  render = priority === 'high' ? 'server-side' : 'client-side',
  ...rest
}: AmplienceResponsiveImageProps) => {
  if (render === 'client-side') {
    return (
      <LazyAmplienceResponsiveImage
        className={className}
        source={source}
        alt={alt}
        loading={loading}
        priority={priority}
        {...rest}
      />
    );
  }

  const sources: (AmplienceResponsiveImageData & { media?: string })[] =
    source instanceof Array ? source : [source];

  // Only single-source images get a CSS ratio: a media-query array may use a different
  // ratio per breakpoint, which a single `aspect-ratio` cannot express.
  const cssAspectRatio =
    source instanceof Array ? undefined : getCssAspectRatio(source.aspectRatio);

  const sourceData = sources
    .map(({ src, widths, sizes, media, ...options }) => {
      if (!src) {
        console.warn('ResponsiveImage: No image provided');
        return null;
      }

      const imageUrl = getAmplienceImageUrl(src, {
        quality: options.quality,
        aspectRatio: options.aspectRatio,
      });

      const srcSet = getAmplienceImageSrcSet(imageUrl, widths);

      return {
        srcSet,
        sizes,
        media,
      };
    })
    .filter((data): data is NonNullable<typeof data> => data !== null);

  if (!sourceData.length) {
    console.warn('ResponsiveImage: No image provided');
    return null;
  }

  if (sourceData.length === 1) {
    return (
      <img
        {...rest}
        srcSet={sourceData[0]!.srcSet}
        sizes={sources[0]!.sizes}
        alt={alt}
        loading={loading ?? 'lazy'}
        fetchPriority={priority}
        className={clsx(
          className
            ? className
            : cssAspectRatio
              ? 'h-auto w-full object-cover'
              : 'size-full object-cover'
        )}
        style={cssAspectRatio ? { aspectRatio: cssAspectRatio, ...rest.style } : rest.style}
      />
    );
  }

  return (
    <picture className={clsx(className ? className : 'size-full object-cover')}>
      {sourceData.map((data, index) => (
        <source {...data} key={index} />
      ))}
      <img
        {...rest}
        srcSet={sourceData[0]!.srcSet}
        sizes={sources[0]!.sizes}
        alt={alt}
        loading={loading ?? 'lazy'}
        fetchPriority={priority}
        className={clsx('text-transparent', className ? className : 'size-full object-cover')}
      />
    </picture>
  );
};
